import { useQueryClient } from '@zg-rentals/react-query5';

import { Button, ButtonGroup, Heading, Modal, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import type {
  MaintenanceTaskStatusDto,
  RentalManagerUserMaintenanceTaskCountDto,
  RentalManagerUserMaintenanceTaskDto,
  RentalManagerUserMaintenanceTaskListDto,
} from '@zg-rentals/rental-manager-env';

import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { useMaintenanceTrackerTaskUserWebApiHandler_delete } from '@zg-rentals/ts-maintenance-tracker-client-react-query';

import {
  emitDeleteTaskFromTaskDetailPageEvent,
  emitDeleteTaskFromTaskTableEvent,
} from '../clickstream/DeleteTaskEvent';
import { TASK_DETAILS_PAGE, TASKS_TABLE, USE_TASKS_LIST } from '../constants/TaskConstants';

type Props = {
  maintenanceTaskId: string;
  onClose: (deleted?: boolean) => void;
  fromExternalApp?: boolean;
  listingAlias?: string;
  clickOriginationPoint?: string;
};

type MaintenanceTask = {
  maintenanceTaskId: string;
  status: MaintenanceTaskStatusDto;
  count: RentalManagerUserMaintenanceTaskCountDto;
};

type MaintenanceTaskSummary = {
  maintenanceTasks: Array<MaintenanceTask>;
};

export const DeleteTaskModal = ({
  maintenanceTaskId,
  onClose,
  fromExternalApp,
  listingAlias,
  clickOriginationPoint,
}: Props) => {
  const queryClient = useQueryClient();
  const { user } = useRentalManagerUser();
  const { enqueueToast } = useToast();

  const { mutate: deleteTask, isPending: isDeleting } = useMaintenanceTrackerTaskUserWebApiHandler_delete();

  return (
    <Modal
      size={{ base: 'fullScreen', md: 'sm' }}
      open={true}
      onOpenChange={(open) => !open && onClose(false)}
      header={<Heading level={1}>Delete this task?</Heading>}
      body={<Paragraph>You won&apos;t be able to view or restore this task if you delete it.</Paragraph>}
      footer={
        <ButtonGroup aria-label="Cancel or delete task">
          <TextButton onClick={() => onClose(false)}>Cancel</TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            loading={isDeleting}
            onClick={() => {
              const queryKey = fromExternalApp ? ['getMaintenanceTasks', listingAlias] : [USE_TASKS_LIST];

              deleteTask(
                { getParams: { maintenanceTaskId } },
                {
                  onSuccess: () => {
                    enqueueToast(<Toast tone="success">Task deleted</Toast>);
                    if (clickOriginationPoint === TASKS_TABLE) {
                      emitDeleteTaskFromTaskTableEvent({ user });
                    } else if (clickOriginationPoint === TASK_DETAILS_PAGE) {
                      emitDeleteTaskFromTaskDetailPageEvent({ user });
                    }

                    let oldData: Array<MaintenanceTask> | Array<RentalManagerUserMaintenanceTaskDto> | undefined;

                    if (fromExternalApp) {
                      oldData = queryClient.getQueryData<MaintenanceTaskSummary>(queryKey)?.maintenanceTasks;
                    } else {
                      oldData = queryClient.getQueryData<RentalManagerUserMaintenanceTaskListDto>(queryKey)?.tasks;
                    }

                    if (!oldData) {
                      return;
                    }
                    const indexOfTaskToDelete = oldData.findIndex(
                      (item) => item.maintenanceTaskId === maintenanceTaskId,
                    );
                    oldData.splice(indexOfTaskToDelete, 1)[0];

                    queryClient.setQueryData(queryKey, {
                      [fromExternalApp ? 'maintenanceTasks' : 'tasks']: oldData,
                    });
                  },
                  onSettled: () => {
                    queryClient.refetchQueries({ queryKey });
                    onClose(true);
                  },
                },
              );
            }}
          >
            Yes, delete
          </Button>
        </ButtonGroup>
      }
    />
  );
};
